import type { FBTProduct } from '@app/pdp/components/FrequentlyBoughtTogether';
import type { Product } from '@app/pdp/types';
import {
    getDefaultCategories,
    type MappedProductVariant,
    type ProductCategories,
    type ProductHit,
    type SelectedVariants,
} from '@libs/algolia';
import type { EventItem } from '@libs/analytics';
import {
    TrackingEvents,
    getNavigationId,
    getNavigationName,
    processFindingMethod,
} from '@libs/analytics';
import type { OcapiProps } from '@libs/common-next';
import { NavigationListTypes } from '@libs/common-next';
import type { Store } from '@libs/common-node';
import { Currency } from '@libs/core';

type CategoryInfo = {
    primaryCategoryId: number | null;
    categories: ProductCategories | null;
};

type AnalyticsInput = {
    product: MappedProductVariant;
    ocapi: OcapiProps | null;
    variant?: SelectedVariants | null;
    categoryInfo: CategoryInfo;
    listInfo: {
        id: string | null;
        name: string | null;
    };
    isDefaultProduct: boolean;
    lastSearchQuery?: string | null;
    eventType?: string;
};

export const getItemCategories = (
    product: MappedProductVariant | undefined
) => ({
    item_category: product?.merchandisingHierarchy?.vicePresidentName ?? '',
    item_category2: product?.merchandisingHierarchy?.division ?? '',
    item_category3: product?.merchandisingHierarchy?.department ?? '',
    item_category4: product?.merchandisingHierarchy?.class ?? '',
    item_category5: product?.merchandisingHierarchy?.category ?? '',
});

export const getItemInfo = <T extends AnalyticsInput>(input: T) => ({
    price: input.product?.price?.number ?? 0,
    quantity: input.ocapi?.basket?.order_total || 1,
    color: input.product?.variants?.color?.display ?? '',
    default_version: input.isDefaultProduct ? 'yes' : 'no',
    size: input.product?.variants?.size?.display ?? '',
    flavor: input.product?.flavor?.value ?? '',
    ...(input.eventType !== TrackingEvents.VIEW_ITEM.toString()
        ? { pharmacy_quantity: input.ocapi?.basket?.order_total || 1 }
        : {}),
    item_list_id: input.listInfo?.id ?? '',
    item_list_name: input.listInfo?.name ?? '',
    item_brand: input.product.brand ?? '',
    item_id: input.product.masterProductID?.toString() ?? '',
    item_name: input.product.name ?? '',
    item_variant: input.product.id.toString() ?? '',
    item_search_term: input?.lastSearchQuery ?? '',
    ...getItemCategories(input.product),
});

export const getEcommerceInfo = ({
    product,
    variant,
    categoryInfo,
    listInfo,
    ocapi,
    isDefaultProduct,
    lastSearchQuery,
    eventType,
}: AnalyticsInput) => {
    const categories = getDefaultCategories(product as ProductHit);

    return {
        currency: Currency[ocapi?.basket?.currency as keyof typeof Currency],
        value:
            (ocapi?.basket?.order_total || 1) *
            Number(product?.price?.number ?? 0),
        pdp_type: NavigationListTypes.STANDARD_PDP,
        ...(eventType !== TrackingEvents.VIEW_ITEM.toString()
            ? { product_recommendation: 'no' }
            : {}),
        category: categories[0]?.name?.default ?? '',
        sub_category1: categories[1]?.name?.default ?? '',
        sub_category2: categories[2]?.name?.default ?? '',
        items: [
            getItemInfo({
                product,
                ocapi,
                variant,
                listInfo,
                categoryInfo,
                isDefaultProduct,
                lastSearchQuery,
                eventType,
            }),
        ],
    };
};

export const mapFBTAddToCartItems = (
    product: Product,
    products: FBTProduct[],
    store: Store | null | undefined,
    itemCartOrder: number,
    selectedDeliveryMethod: string,
    lastSearchQuery: string,
    autoShipFrequency?: number | undefined
): EventItem[] => {
    const findingMethod = processFindingMethod(
        window.sessionStorage.pdpFindingMethod
    );

    return products.map(item => ({
        item_name: item.name,
        item_id: item.masterProductID?.toString(),
        quantity: 1,
        price: item.price?.number,
        ...getItemCategories(item),
        item_brand: product.brand,
        item_variant: item.objectID,
        item_list_id: getNavigationId(findingMethod),
        item_list_name: getNavigationName(findingMethod),
        fulfillment_method: selectedDeliveryMethod,
        autoship_frequency: autoShipFrequency || '',
        store_id: store?.StoreNumber.toString(),
        size: (Object.values(product.size ?? {})[0] as string) ?? '',
        color: product.color?.value ?? '',
        flavor: product.flavor?.value ?? '',
        default_version: product.id === +item.objectID ? 'yes' : 'no',
        item_search_term: lastSearchQuery,
        item_cart_order: itemCartOrder,
    })) as EventItem[];
};

type AddToCartItems = {
    product: Product;
    selectedProduct: MappedProductVariant;
    quantity?: number;
    store: Store | null | undefined;
    cartCount?: number;
    selectedDeliveryMethod: string;
    lastSearchQuery: string;
    frequency?: number;
};

export const mapAddToCartItems = ({
    product,
    selectedProduct,
    quantity,
    store,
    cartCount,
    selectedDeliveryMethod,
    lastSearchQuery,
    frequency,
}: AddToCartItems) => {
    const findingMethod = processFindingMethod(
        window.sessionStorage.pdpFindingMethod
    );

    return {
        item_name: selectedProduct.name ?? '',
        item_id: String(product.masterProductID?.toString()),
        quantity: quantity ?? 0,
        price: selectedProduct.price?.number ?? 0,
        ...getItemCategories(product),
        item_brand: selectedProduct.brand ?? '',
        item_variant: String(selectedProduct.id),
        item_list_id: getNavigationId(findingMethod),
        item_list_name: getNavigationName(findingMethod),
        fulfillment_method: selectedDeliveryMethod,
        autoship_frequency: frequency,
        store_id: store?.StoreNumber.toString(),
        color: selectedProduct.color?.value ?? '',
        size: (Object.values(selectedProduct.size ?? {})[0] as string) ?? '',
        flavor: selectedProduct.flavor?.value ?? '',
        default_version: selectedProduct.id === product.id ? 'yes' : 'no',
        item_search_term: lastSearchQuery,
        item_cart_order: cartCount,
    };
};
